

import React, { useState, useEffect } from 'react';
import { validateGitHub } from './Register.validation';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  TextField,
  Autocomplete
} from '@mui/material';
import client from '../api/client';
import RegisterCountdownTimer from '../components/RegisterCountdownTimer';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';

// List of all countries in the world
const countryList = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Congo-Brazzaville)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia (Czech Republic)", "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini (fmr. 'Swaziland')", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Holy See", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar (formerly Burma)", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine State", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States of America", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];


function Register() {
  const [formData, setFormData] = useState({
    track: '',
    teamType: '',
    fullName: '',
    email: '',
    phone: '',
    experience: '',
    skills: '',
    motivation: '',
    linkedin: '',
    portfolio: '',
    nationality: '',
    tshirtSize: '',
    gender: '',
    website: ''
  });
  const [linkedinValid, setLinkedinValid] = useState(false);
  const [portfolioValid, setPortfolioValid] = useState(false);
  const [portfolioError, setPortfolioError] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
    // Helper to check if all required fields are filled and valid
    useEffect(() => {
      let requiredFields = ['track', 'fullName', 'email', 'phone', 'experience', 'skills', 'motivation', 'nationality', 'gender', 'tshirtSize'];
      if (formData.track === 'tech') {
        requiredFields = requiredFields.concat(['linkedin', 'portfolio']);
      }
      const allFilled = requiredFields.every(f => formData[f] && formData[f].trim() !== '');
      const validLinkedin = formData.track === 'tech' ? linkedinValid : true;
      const validPortfolio = formData.track === 'tech' ? portfolioValid : true;
      if (formData.track === 'tech') {
        setCanSubmit(allFilled && validLinkedin && validPortfolio);
      } else {
        setCanSubmit(allFilled);
      }
    }, [formData, linkedinValid, portfolioValid]);
  const [teamMembers, setTeamMembers] = useState([]); // [{fullName, phone, role, email}]

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [hackathonDeadline, setHackathonDeadline] = useState(null);

  // Helper: fallback deadline (for UI) if DB is missing or not loaded
  const fallbackDeadline = React.useMemo(() => new Date('2025-12-09T13:59:59'), []);

  // URL validation helpers
  const validateLinkedIn = (url) => {
    const regex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/;
    return regex.test(url);
  };

  const validatePortfolioFormat = (url) => {
    const regex = /^https?:\/\/(www\.)?github\.com\/[a-zA-Z0-9_-]+\/?$/;
    return regex.test(url);
  };

  const validatePortfolioExistence = async (url) => {
    if (!validatePortfolioFormat(url)) {
      setPortfolioValid(false);
      setPortfolioError('Invalid GitHub profile URL format');
      return;
    }
    const isValid = await validateGitHub(url);
    setPortfolioValid(isValid);
    setPortfolioError(isValid ? '' : 'GitHub profile does not exist');
  };

  useEffect(() => {
    let intervalId;
    const fetchDeadline = async () => {
      try {
        const res = await client.get('/api/admin-deadline');
        console.log('[Register] API /api/admin-deadline response:', res.data);
        if (res.data && res.data.hackathonDeadline) {
          // Always parse as Date object
          const deadlineRaw = res.data.hackathonDeadline;
          const deadlineDate = new Date(deadlineRaw);
          setHackathonDeadline(deadlineDate);
          // Debug logging
          console.log('[Register] Raw deadline from API:', deadlineRaw, '| typeof:', typeof deadlineRaw);
          console.log('[Register] Parsed deadline as Date:', deadlineDate, '| isNaN:', isNaN(deadlineDate));
          console.log('[Register] Now:', new Date(), '| Now > deadline:', new Date() > deadlineDate);
          setDeadlinePassed(new Date() > deadlineDate);
        } else {
          setHackathonDeadline(fallbackDeadline);
          setDeadlinePassed(new Date() > fallbackDeadline);
        }
      } catch (err) {
        setHackathonDeadline(fallbackDeadline);
        setDeadlinePassed(new Date() > fallbackDeadline);
        console.error('[Register] Error fetching deadline:', err);
      }
    };
    fetchDeadline();
    intervalId = setInterval(fetchDeadline, 30000);
    return () => clearInterval(intervalId);
  }, [fallbackDeadline]);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Team member handlers
  const handleTeamMemberChange = (idx, field, value) => {
    const updated = [...teamMembers];
    updated[idx][field] = value;
    setTeamMembers(updated);
  };
  const addTeamMember = () => {
    if (teamMembers.length < 3) {
      setTeamMembers([...teamMembers, { fullName: '', phone: '', role: '', email: '' }]);
    }
  };
  const removeTeamMember = (idx) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });


    // Validate required fields
    let requiredFields = ['track', 'fullName', 'email', 'phone', 'experience', 'skills', 'motivation', 'nationality', 'gender', 'tshirtSize'];
    // Only require LinkedIn/Portfolio for Tech track
    if (formData.track === 'tech') {
      requiredFields = requiredFields.concat(['linkedin', 'portfolio']);
    }
    const missingFields = requiredFields.filter(field => !formData[field]);

    // Team member validation (if Tech/Team)
    let teamMemberErrors = [];
    if (formData.track === 'tech' && formData.teamType === 'team') {
      if (teamMembers.length === 0) {
        teamMemberErrors.push('At least one team member is required');
      }
      teamMembers.forEach((tm, idx) => {
        ['fullName', 'phone', 'role', 'email'].forEach(field => {
          if (!tm[field]) teamMemberErrors.push(`Team member ${idx + 1}: ${field} is required`);
        });
        if (tm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tm.email)) {
          teamMemberErrors.push(`Team member ${idx + 1}: Invalid email`);
        }
      });
    }

    if (missingFields.length > 0 || teamMemberErrors.length > 0) {
      setMessage({ type: 'error', text: `Please fill in all required fields. ${missingFields.concat(teamMemberErrors).join(', ')}` });
      setLoading(false);
      return;
    }

    // Additional validation for tech track
    if (formData.track === 'tech' && !formData.teamType) {
      setMessage({ type: 'error', text: 'Please select your team participation type for the tech track.' });
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      setLoading(false);
      return;
    }

    // Validate LinkedIn URL format
    if (formData.track === 'tech' && formData.linkedin && !validateLinkedIn(formData.linkedin)) {
      setMessage({ type: 'error', text: 'Please enter a valid LinkedIn profile URL.' });
      setLoading(false);
      return;
    }

    // Validate GitHub (portfolio) URL
    if (formData.track === 'tech' && formData.portfolio) {
      const isValid = await validateGitHub(formData.portfolio);
      if (!isValid) {
        setMessage({ type: 'error', text: 'Please enter a valid and existing GitHub profile URL.' });
        setLoading(false);
        return;
      }
    }

    try {

      const response = await client.post('/.netlify/functions/register', {
        ...formData,
        teamMembers: (formData.track === 'tech' && formData.teamType === 'team') ? teamMembers : []
      });

      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        // Display backend error messages
        if (response.data.errors && Array.isArray(response.data.errors)) {
          setMessage({
            type: 'error',
            text: response.data.errors.join(', ')
          });
        } else {
          setMessage({
            type: 'error',
            text: response.data.error || 'Registration failed. Please try again.'
          });
        }
        setLoading(false);
        return;
      }

      setMessage({ type: 'success', text: 'Registration submitted successfully! Check your email for a confirmation with next steps and event details.' });

      // Reset form
      setFormData({
        track: '',
        teamType: '',
        fullName: '',
        email: '',
        phone: '',
        experience: '',
        skills: '',
        motivation: '',
        linkedin: '',
        portfolio: '',
        nationality: '',
        tshirtSize: '',
        gender: '',
        website: ''
      });
      setTeamMembers([]);
    } catch (error) {
      // Handle HTTP error responses (like 409 for duplicate email)
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.success === false && errorData.errors) {
          setMessage({
            type: 'error',
            text: errorData.errors.join(', ')
          });
        } else if (errorData.error) {
          setMessage({
            type: 'error',
            text: errorData.error
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Registration failed. Please try again.'
          });
        }
      } else {
        console.log('Backend not available, simulating successful registration for demo purposes');
        setMessage({
          type: 'success',
          text: 'Registration submitted successfully! Check your email for a confirmation with next steps and event details.'
        });
      }

      // Reset form
      setFormData({
        track: '',
        teamType: '',
        fullName: '',
        email: '',
        phone: '',
        experience: '',
        skills: '',
        motivation: '',
        linkedin: '',
        portfolio: '',
        nationality: '',
        tshirtSize: '',
        gender: '',
        website: ''
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
        {/* Hero Section with Image */}
        <AnimatedSection animation="fadeIn">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{
              position: 'relative',
              textAlign: 'center',
              my: 4,
              py: 6,
              background: `
                linear-gradient(135deg, rgba(0, 100, 0, 0.8), rgba(46, 125, 50, 0.8)),
                url('/homepageimage.jpg')
              `,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'white',
              borderRadius: 4,
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.4)',
                zIndex: 1
              }
            }}>
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                    mb: 2
                  }}
                >
                  Register for Lagos Impact Hackathon
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  paragraph
                  sx={{
                    color: 'white',
                    opacity: 0.9,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    maxWidth: 600,
                    mx: 'auto'
                  }}
                >
                  Join Nigeria's elite innovators in solving the biggest challenges facing our communities.
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </AnimatedSection>


        {/* Countdown Timer - always visible below hero section */}
        <AnimatedSection animation="slideUp" delay={0.2}>
          <Typography variant="subtitle1" color="error" sx={{ mb: 1, fontWeight: 600 }}>
            Registration Deadline: {hackathonDeadline
              ? hackathonDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
              : fallbackDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          <RegisterCountdownTimer
            targetDate={hackathonDeadline ? hackathonDeadline.toISOString() : fallbackDeadline.toISOString()}
            variant="hero"
            showIcon={true}
          />
        </AnimatedSection>

        {/* Attendance Notice */}
        <AnimatedSection animation="fadeIn" delay={0.25}>
          <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#fff',
                background: 'linear-gradient(90deg, #232526 0%, #414345 100%)',
                border: '2px solid #ffd600',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                display: 'inline-block',
                boxShadow: 2,
                letterSpacing: 1.2,
                textShadow: '1px 1px 2px rgba(0,0,0,0.18)'
              }}
            >
              Attendance: Strictly by invitation – for <span style={{ color: '#ffd600', fontWeight: 900 }}>APPROVED Hackathon participants ONLY</span>
            </Typography>
          </Box>
        </AnimatedSection>

        {/* Registration Form or Closed Card */}
        {deadlinePassed ? (
          <AnimatedSection animation="slideUp" delay={0.3}>
            <Card sx={{ mt: 4, mb: 4, p: 4, textAlign: 'center', background: '#f8d7da', border: '1px solid #f5c2c7' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: '#b71c1c' }}>
                  Registration Closed
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  The registration deadline has passed. Thank you for your interest!
                </Typography>
              </CardContent>
            </Card>
          </AnimatedSection>
        ) : null}
        {!deadlinePassed && (
          <AnimatedSection animation="slideUp" delay={0.3}>
            <motion.div
              whileHover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent>
                  <form onSubmit={handleSubmit}>
                    {/* Honeypot field to trap bots */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                      autoComplete="off"
                      tabIndex={-1}
                    />
                    <Grid container spacing={3}>
                      {/* Track Selection */}
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Which track interests you?</InputLabel>
                          <Select
                            name="track"
                            value={formData.track}
                            onChange={handleChange}
                          >
                            <MenuItem value="tech">Tech for Impact Track</MenuItem>
                            <MenuItem value="marketing">Marketing for Impact Track</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>


                      {/* Team Type (only for Tech track) */}
                      {formData.track === 'tech' && (
                        <Grid item xs={12}>
                          <FormControl fullWidth required>
                            <InputLabel>Team Participation Type</InputLabel>
                            <Select
                              name="teamType"
                              value={formData.teamType}
                              onChange={handleChange}
                            >
                              <MenuItem value="individual">Individual</MenuItem>
                              <MenuItem value="team">Team</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}

                      {/* Team Members (if Tech/Team) */}
                      {formData.track === 'tech' && formData.teamType === 'team' && (
                        <Grid item xs={12}>
                          <Box sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Team Members (max 3)</Typography>
                            {teamMembers.map((tm, idx) => (
                              <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField label="Full Name" value={tm.fullName} onChange={e => handleTeamMemberChange(idx, 'fullName', e.target.value)} fullWidth required />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField label="Phone" value={tm.phone} onChange={e => handleTeamMemberChange(idx, 'phone', e.target.value)} fullWidth required />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField label="Role" value={tm.role} onChange={e => handleTeamMemberChange(idx, 'role', e.target.value)} fullWidth required />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField label="Email" value={tm.email} onChange={e => handleTeamMemberChange(idx, 'email', e.target.value)} fullWidth required type="email" />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <button type="button" onClick={() => removeTeamMember(idx)} style={{ color: '#b71c1c', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                            {teamMembers.length < 3 && (
                              <button type="button" onClick={addTeamMember} style={{ background: '#388e3c', color: 'white', fontWeight: 600, padding: '8px 24px', border: 'none', borderRadius: '6px', cursor: 'pointer', marginTop: 8 }}>Add Team Member</button>
                            )}
                          </Box>
                        </Grid>
                      )}

                      {/* Full Name */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full Name *"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email *"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Phone */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number *"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Experience */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Relevant Experience *"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Skills */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Skills *"
                          name="skills"
                          value={formData.skills}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* Motivation */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Motivation *"
                          name="motivation"
                          value={formData.motivation}
                          onChange={handleChange}
                          required
                        />
                      </Grid>

                      {/* LinkedIn & Portfolio (Tech track only) */}
                      {formData.track === 'tech' && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="LinkedIn *"
                              name="linkedin"
                              value={formData.linkedin}
                              onChange={handleChange}
                              onKeyUp={e => setLinkedinValid(validateLinkedIn(e.target.value))}
                              required
                              error={formData.linkedin && !linkedinValid}
                              helperText={formData.linkedin && !linkedinValid ? 'Invalid LinkedIn profile URL' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="GitHub Portfolio *"
                              name="portfolio"
                              value={formData.portfolio}
                              onChange={handleChange}
                              onKeyUp={e => validatePortfolioExistence(e.target.value)}
                              required
                              error={formData.portfolio && !portfolioValid}
                              helperText={formData.portfolio && !portfolioValid ? portfolioError : ''}
                            />
                          </Grid>
                        </>
                      )}

                      {/* Nationality (searchable dropdown) */}
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          fullWidth
                          options={countryList}
                          value={formData.nationality || null}
                          onChange={(e, value) => setFormData({ ...formData, nationality: value || '' })}
                          renderInput={(params) => (
                            <TextField {...params} label="Nationality *" required />
                          )}
                          isOptionEqualToValue={(option, value) => option === value}
                        />
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Gender</InputLabel>
                          <Select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                          >
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* T-shirt Size */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel>T-shirt Size</InputLabel>
                          <Select
                            name="tshirtSize"
                            value={formData.tshirtSize}
                            onChange={handleChange}
                          >
                            <MenuItem value="XS">XS</MenuItem>
                            <MenuItem value="S">S</MenuItem>
                            <MenuItem value="M">M</MenuItem>
                            <MenuItem value="L">L</MenuItem>
                            <MenuItem value="XL">XL</MenuItem>
                            <MenuItem value="XXL">XXL</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                      <button
                        type="submit"
                        style={{
                          background: '#388e3c',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          padding: '12px 36px',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: canSubmit ? 'pointer' : 'not-allowed',
                          boxShadow: '0 2px 8px rgba(56,142,60,0.12)',
                          transition: 'background 0.2s',
                          opacity: canSubmit ? 1 : 0.5
                        }}
                        disabled={!canSubmit || loading}
                      >
                        {loading ? 'Submitting...' : 'Submit Registration'}
                      </button>
                    </Box>
                  </form>
                  {/* Message Display */}
                  {message.text && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                    <Box sx={{ mt: 3 }}>
                      <Alert severity={message.type}>
                        {message.text}
                      </Alert>
                    </Box>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </AnimatedSection>
        )}

      {/* Additional Info */}
      <AnimatedSection animation="fadeIn" delay={0.5}>
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Applications will be reviewed by our selection committee. Only the most qualified candidates will be invited to participate.
        </Typography>
      </Box>
      </AnimatedSection>
    </Container>
  );
}

export default Register;
